// Centralized content for Paula's Cleaning Services demo site.
// NOTE: Service records are now stored in MongoDB and served via
// GET /api/public/services. See @/contexts/ServicesContext for the
// single source of truth. The constants below cover other static content
// (commercial copy, pricing tiers, addons fallback, images).
import {
  Home as HomeIcon,
  Sparkles,
  Building2,
  Repeat,
  Brush,
  ClipboardList,
  Briefcase,
} from "lucide-react";

export const BUSINESS = {
  name: "P & A Cleaning",
  full: "P & A Cleaning / Paula's Cleaning Service",
  short: "P & A Cleaning",
  area: "Sacramento and surrounding areas",
  phone: "916-224-7260",
  phoneHref: "tel:9162247260",
  email: "Aprilberches@att.net",
  emailHref: "mailto:Aprilberches@att.net",
  website: "pacleans.com",
  instagram: "@pacleaning_",
  instagramUrl: "https://instagram.com/pacleaning_",
  facebook: "@paulacleans",
  facebookUrl: "https://facebook.com/paulacleans",
  yelpUrl: "https://m.yelp.com/biz/paulas-cleaning-service-arden-arcade",
};

export const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Commercial Cleaning", to: "/commercial-cleaning" },
  { label: "Pricing", to: "/pricing" },
  { label: "Request Estimate", to: "/request-estimate" },
  { label: "FAQ", to: "/faq" },
  { label: "Reviews", to: "/reviews" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];


export const TIERS = [
  {
    name: "Basic Refresh",
    tagline: "Light cleaning or recurring service.",
    bullets: [
      "Surface cleaning of kitchens & baths",
      "Dusting and vacuuming",
      "High-touch points wipe-down",
      "Best for already-tidy homes",
    ],
    cta: "Request Estimate",
    badge: "Light",
    note: "Final quote required.",
  },
  {
    name: "Standard Move-Out / Pre-Listing",
    tagline: "Vacant homes, rentals, sale prep, and turnovers.",
    bullets: [
      "Detailed kitchen & bath clean",
      "Inside cabinets and drawers (vacant)",
      "Baseboards, doors, switches",
      "Photo-ready finish",
    ],
    cta: "Request Estimate",
    badge: "Popular",
    popular: true,
    note: "Most common option.",
  },
  {
    name: "Detailed / Heavy-Duty Cleaning",
    tagline: "Heavy buildup, pet hair, dust, appliance interiors.",
    bullets: [
      "Extra time on neglected areas",
      "Appliance interiors on request",
      "Garage cleaning available",
      "Photos or inspection may be required",
    ],
    cta: "Request Estimate",
    badge: "Detailed",
    note: "Photos or inspection may be required.",
  },
];

export const SQFT_TABLE = [
  { label: "Under 1,200 sq ft", price: "$200 – $325" },
  { label: "1,200 – 1,999 sq ft", price: "$350 – $450" },
  { label: "2,000+ sq ft", price: "$450 – $550+" },
];

export const MOVE_PRICING = [
  { label: "Studio or 1 bed / 1 bath", price: "Starting around $250" },
  { label: "Standard home under 1,200 sq ft", price: "Typically $300 – $325+" },
  { label: "1,200 – 1,600 sq ft", price: "Typically $350+" },
  { label: "1,600 – 2,000 sq ft", price: "Typically $400+" },
  { label: "2,000+ sq ft", price: "Typically $450 – $550+" },
];

export const ADDONS = [
  { label: "Inside fridge", price: "$25 – $35" },
  { label: "Inside kitchen cabinets", price: "$25 – $35" },
  { label: "Inside oven", price: "$25 – $35" },
  { label: "Blinds", price: "$5 – $10 per set" },
  { label: "Window sills", price: "$3 – $5" },
  { label: "Garage cleaning", price: "$20 – $30+" },
  { label: "Carpet cleaning", price: "$150 – $400 whole house, or $0.25 – $0.50 / sq ft" },
  { label: "Junk removal", price: "Custom quote required" },
  { label: "Yard cleanup, mowing, edging", price: "Custom quote required" },
  { label: "Commercial cleaning", price: "Custom quote required" },
];

export const WHO_WE_SERVE = [
  { label: "Offices", icon: Briefcase },
  { label: "Small businesses", icon: Building2 },
  { label: "Property management offices", icon: ClipboardList },
  { label: "Real estate offices", icon: HomeIcon },
  { label: "Retail spaces", icon: Sparkles },
  { label: "Salons & barbershops", icon: Brush },
  { label: "Studios", icon: Sparkles },
  { label: "Showrooms", icon: Building2 },
  { label: "Common areas", icon: Repeat },
  { label: "Light commercial spaces", icon: Briefcase },
];

export const COMMERCIAL_INCLUDES = [
  "Office cleaning",
  "Lobby / reception cleaning",
  "Trash removal",
  "Vacuuming",
  "Mopping",
  "Restroom cleaning",
  "Breakroom / kitchenette cleaning",
  "Desk and surface dusting",
  "Door handles and high-touch surfaces",
  "Common area cleaning",
  "Special requests by quote",
];

export const COMMERCIAL_FREQ = [
  "One-time cleaning",
  "Weekly cleaning",
  "Biweekly cleaning",
  "Monthly cleaning",
  "Custom recurring schedule",
];

export const PRICING_DISCLAIMER =
  "Prices shown are ballpark estimates only. Final pricing is per job, not per hour, and may change after inspection or review of photos. Final quote depends on square footage, number of bedrooms and bathrooms, home condition, occupancy, access, utilities, trash or junk, pet hair, heavy buildup, appliance interiors, carpet cleaning, yard cleanup, and special requests.";

export const COMMERCIAL_DISCLAIMER =
  "Commercial cleaning pricing is customized based on square footage, business type, cleaning frequency, number of restrooms, floor type, scope of work, access requirements, supplies, and property condition. Final pricing is provided after a walkthrough, inspection, or review of photos.";

export const IMAGES = {
  heroResidential:
    "https://images.pexels.com/photos/28586197/pexels-photo-28586197.jpeg",
  heroCommercial:
    "https://images.pexels.com/photos/10567236/pexels-photo-10567236.jpeg",
  team: "https://images.pexels.com/photos/9462761/pexels-photo-9462761.jpeg",
  residentialService:
    "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
  commercialService:
    "https://images.pexels.com/photos/10567360/pexels-photo-10567360.jpeg",
  kitchen:
    "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1400&q=80",
  office:
    "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1400&q=80",
};
